import React, { useState } from "react";
import { Stack, Text, Depths, Separator, mergeStyles, mergeStyleSets, CommandBarButton, IButtonStyles, useTheme } from "@fluentui/react";
import DaneValidationResultMxEntity from "./DaneValidationResultMxEntity";
import DaneValidationResultTestLabels from "./DaneValidationResultTestLabels";
import { Utils } from "../../Utils";
import { Helpers } from "./helpers/Helpers";
import { IDaneValidationTestType } from "../../constants/RcaDaneValidationTestTypes";
import { IRcaDaneValidationTestDomainDetails } from "../../models/IRcaDaneValidationTestDomainDetails";
import { ComponentDecorators } from "../ComponentDecorators";

const commandBarButtonStyle: IButtonStyles = {
  textContainer: {
    fontSize: "x-large",
    textTransform: "uppercase",
    marginRight: 12,
    paddingTop: 3,
    paddingBottom: 3,
    fontWeight: "700",
  },
  root: {
    backgroundColor: "inherit",
  },
  rootHovered: {
    backgroundColor: "inherit",
  },
  rootPressed: {
    backgroundColor: "inherit",
  },
};

/**
 * The domain result UI
 *
 * @param props - The component properties
 * @returns The markup
 */
const DaneValidationResultItem: React.FC<{
  /**
   * List of items to render in the table
   */
  result: IRcaDaneValidationTestDomainDetails;
  /**
   * Test Type of the result
   */
  testType: IDaneValidationTestType;
}> = ({ result, testType }) => {
  const theme = useTheme();
  const classNames = mergeStyleSets({
    commandBar: {
      margin: 0,
      padding: 0,
      height: 44,
      selectors: { [Utils.mobileQuery]: { height: 35 } },
    },
    domainTitleWrapper: {
      cursor: "pointer",
      display: "flex",
      flexWrap: "wrap",
      gap: "8px",
      backgroundColor: `var(--rca-control-background, ${theme.semanticColors.bodyBackground})`,
      ":hover": {
        backgroundColor: theme.semanticColors.bodyBackgroundHovered,
      },
    },
    domainInfoWrapper: {
      width: "100%",
      backgroundColor: `var(--rca-card-background, ${theme.semanticColors.bodyBackground})`,
      border: "1px solid var(--rca-card-border, transparent)",
      borderRadius: 2,
      boxShadow: `var(--rca-card-shadow, ${Depths.depth4})`,
      padding: 15,
      marginBottom: 20,
      height: "fit-content",
      transition: ".2s",
      overflow: "hidden",
    },
    domainInfoWrapperCollapsed: {
      //maxHeight: 60,
    },
    mxRecordsTable: {},
    mxRecordsTableCollapsed: {
      display: "none",
    },
  });

  const [collapsed, setCollapsed] = useState(true);

  const response = result.MxRecordsResponse;
  const record = result.BestMatchMxRecord;
  const domainDnssecResult = result.DnsSecStatus;
  const summaryText = Helpers.getSummaryText(testType, response, record, domainDnssecResult);

  const toggleCollapsed = (): void => setCollapsed((prev) => !prev);

  const handleTreeItemKeyDown = (ev: React.KeyboardEvent<HTMLElement>): void => {
    if (ev.target !== ev.currentTarget) {
      return;
    }

    if (ev.key === "Enter" || ev.key === " " || ev.key === "Spacebar") {
      ev.preventDefault();
      toggleCollapsed();
    }
  };

  const expandCollapseButtonTitle = (collapsed ? ClientStrings.ExpandNode : ClientStrings.CollapseNode).concat(" ", result.Domain);
  return (
    <Stack
      className={mergeStyles(classNames.domainInfoWrapper, collapsed ? classNames.domainInfoWrapperCollapsed : {})}
      role="treeitem"
      tabIndex={-1}
      aria-expanded={!collapsed}
      aria-label={`${result.Domain}: ${summaryText}`}
      onKeyDown={handleTreeItemKeyDown}
    >
      <Stack horizontal verticalAlign="center" className={classNames.domainTitleWrapper} onClick={toggleCollapsed}>
        <CommandBarButton
          styles={commandBarButtonStyle}
          ariaLabel={expandCollapseButtonTitle}
          aria-expanded={!collapsed}
          text={result.Domain}
          iconProps={{ iconName: collapsed ? "ChevronDownSmall" : "ChevronUpSmall" }}
        />
        <DaneValidationResultTestLabels response={response} record={record} testType={testType} domainDnsSecResult={domainDnssecResult} />

        {ComponentDecorators.replaceLinks(<Text variant="medium">{summaryText}</Text>)}
      </Stack>
      <Stack className={mergeStyles(classNames.mxRecordsTable, collapsed ? classNames.mxRecordsTableCollapsed : {})}>
        <Separator styles={{ root: { height: 5, padding: 0, margin: 0, marginTop: 0 } }} />
        <Text variant="mediumPlus" block styles={{ root: { paddingBottom: 20, paddingTop: 20 } }}>
          {record ? ClientStrings.DaneValidationPresentSMTPServers : ClientStrings.DaneValidationNoSMTPServers}
        </Text>
        {response.Records.map((record) => {
          return <DaneValidationResultMxEntity response={response} record={record} key={record.Domain} testType={testType} />;
        })}
      </Stack>
    </Stack>
  );
};

export default DaneValidationResultItem;
